/**
 * Player name matching (§7.4, FR-17 – FR-20).
 *
 * This is treated as a first-class feature, not a utility. Every external
 * ranking source arrives as text, and a name that fails to resolve is a player
 * missing from the board on draft night. The rule the whole module is built
 * around: **never silently drop a ranked player.** Anything that does not
 * resolve comes back in `unmatched` with fuzzy suggestions attached.
 *
 * Pure module — no I/O.
 */

import type { PlayerId, Player, PlayerUniverse, Position, UnmatchedRow } from './types.ts';
import { FANTASY_POSITIONS } from './types.ts';

/** Name suffixes that carry no identity and are dropped during normalization. */
const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);

/** Tokens that mark a team-defense row rather than a person (FR-18). */
const DST_TOKENS = new Set(['dst', 'def', 'd', 'st', 'defense', 'defence', 'dnst']);

/**
 * NFL team abbreviations as Sleeper spells them, plus the common alternates
 * other sources use. Used both for normalizing a team column and for detecting
 * *which* column is the team column during BYOR extraction (§4.8.2).
 */
export const TEAM_ALIASES: Record<string, string> = {
  ARI: 'ARI', ARZ: 'ARI',
  ATL: 'ATL',
  BAL: 'BAL', BLT: 'BAL',
  BUF: 'BUF',
  CAR: 'CAR',
  CHI: 'CHI',
  CIN: 'CIN',
  CLE: 'CLE', CLV: 'CLE',
  DAL: 'DAL',
  DEN: 'DEN',
  DET: 'DET',
  GB: 'GB', GNB: 'GB',
  HOU: 'HOU', HST: 'HOU',
  IND: 'IND',
  JAX: 'JAX', JAC: 'JAX',
  KC: 'KC', KAN: 'KC',
  LV: 'LV', LVR: 'LV', OAK: 'LV',
  LAC: 'LAC', SD: 'LAC', SDG: 'LAC',
  LAR: 'LAR', LA: 'LAR', STL: 'LAR',
  MIA: 'MIA',
  MIN: 'MIN',
  NE: 'NE', NWE: 'NE',
  NO: 'NO', NOR: 'NO',
  NYG: 'NYG',
  NYJ: 'NYJ',
  PHI: 'PHI',
  PIT: 'PIT',
  SEA: 'SEA',
  SF: 'SF', SFO: 'SF',
  TB: 'TB', TAM: 'TB',
  TEN: 'TEN',
  WAS: 'WAS', WSH: 'WAS', WFT: 'WAS',
  FA: 'FA',
};

/** Full city/nickname → abbreviation, for DST rows spelled out in prose. */
export const TEAM_NAMES: Record<string, string> = {
  cardinals: 'ARI', falcons: 'ATL', ravens: 'BAL', bills: 'BUF', panthers: 'CAR',
  bears: 'CHI', bengals: 'CIN', browns: 'CLE', cowboys: 'DAL', broncos: 'DEN',
  lions: 'DET', packers: 'GB', texans: 'HOU', colts: 'IND', jaguars: 'JAX',
  chiefs: 'KC', raiders: 'LV', chargers: 'LAC', rams: 'LAR', dolphins: 'MIA',
  vikings: 'MIN', patriots: 'NE', saints: 'NO', giants: 'NYG', jets: 'NYJ',
  eagles: 'PHI', steelers: 'PIT', seahawks: 'SEA', '49ers': 'SF', niners: 'SF',
  buccaneers: 'TB', bucs: 'TB', titans: 'TEN', commanders: 'WAS',
  arizona: 'ARI', atlanta: 'ATL', baltimore: 'BAL', buffalo: 'BUF',
  carolina: 'CAR', chicago: 'CHI', cincinnati: 'CIN', cleveland: 'CLE',
  dallas: 'DAL', denver: 'DEN', detroit: 'DET', houston: 'HOU',
  indianapolis: 'IND', jacksonville: 'JAX', miami: 'MIA', minnesota: 'MIN',
  pittsburgh: 'PIT', seattle: 'SEA', tennessee: 'TEN', washington: 'WAS',
  philadelphia: 'PHI',
};

export function normalizeTeam(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const t = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!t) return null;
  return TEAM_ALIASES[t] ?? null;
}

export function isTeamAbbrev(raw: string): boolean {
  return normalizeTeam(raw) !== null;
}

export function normalizePosition(raw: string | null | undefined): Position | null {
  if (!raw) return null;
  const p = raw.trim().toUpperCase().replace(/[^A-Z]/g, '');
  if (!p) return null;
  if (p === 'PK') return 'K';
  if (p === 'DST' || p === 'DEF' || p === 'DEFENSE' || p === 'DST' || p === 'TEAMDEFENSE') return 'DEF';
  if (p === 'FB') return 'RB';
  return (FANTASY_POSITIONS as readonly string[]).includes(p) ? (p as Position) : null;
}

/**
 * Strip the trailing annotations sources love to append (§5.3):
 *   "Jahmyr Gibbs DET (RB1)"  → "Jahmyr Gibbs"
 *   "Ja'Marr Chase, CIN - WR" → "Ja'Marr Chase"
 *   "Bijan Robinson (ATL)"    → "Bijan Robinson"
 *
 * Deliberately conservative: it only removes a trailing fragment when every
 * token in that fragment is a team abbreviation, a position, or a position-rank
 * token like "RB1". Otherwise a real surname could be eaten.
 */
export function stripAnnotations(raw: string): string {
  let s = raw.trim();

  // Remove any parenthesised/bracketed trailing group whose contents are all
  // team/position/rank tokens.
  s = s.replace(/[([{]([^)\]}]*)[)\]}]\s*$/g, (whole, inner: string) => {
    const tokens = inner.split(/[\s,/|-]+/).filter(Boolean);
    if (tokens.length === 0) return whole;
    return tokens.every(isMetaToken) ? '' : whole;
  });

  s = s.trim().replace(/[,;|/\\-]+\s*$/, '').trim();

  // Peel trailing meta tokens one at a time, but never take the last two
  // remaining tokens — "Miami Dolphins" must survive even though both look
  // team-ish, and a one-token name is never valid.
  for (;;) {
    const tokens = s.split(/\s+/).filter(Boolean);
    if (tokens.length <= 2) break;
    const last = tokens[tokens.length - 1]!;
    if (!isMetaToken(last)) break;
    tokens.pop();
    s = tokens.join(' ').replace(/[,;|/\\-]+$/, '').trim();
  }

  return s.replace(/\s+/g, ' ').trim();
}

function isMetaToken(token: string): boolean {
  const t = token.trim().replace(/[.,;:|/\\()[\]{}]/g, '');
  if (!t) return false;
  if (isTeamAbbrev(t)) return true;
  if (normalizePosition(t) !== null) return true;
  // Position-rank tokens: RB1, WR12, QB3, TE10, FLEX2
  if (/^(QB|RB|WR|TE|K|PK|DEF|DST|FLEX|SFLX)\d{1,3}$/i.test(t)) return true;
  // Bare bye-week annotations: "BYE", "BYE 9", "(9)" handled by caller
  if (/^bye$/i.test(t)) return true;
  return false;
}

/**
 * Canonical name form used for equality comparisons (FR-18).
 *
 * Handles: diacritics, apostrophes and punctuation, generational suffixes,
 * initial styles (`D.K.` / `DK` / `D K`), and casing. The result contains only
 * `[a-z0-9]`, matching the shape of Sleeper's own `search_full_name`, which
 * lets exact matches hit on the first pass for the overwhelming majority of
 * rows.
 */
export function normalizeName(raw: string): string {
  if (!raw) return '';
  let s = raw.normalize('NFKD').replace(/[\u0300-\u036f]/g, ''); // strip diacritics
  s = s.toLowerCase();
  s = s.replace(/[\u2018\u2019\u0027`\u00b4]/g, ''); // Ja'Marr -> jamarr
  s = s.replace(/&/g, ' and ');
  s = s.replace(/[^a-z0-9\s.-]/g, ' ');
  s = s.replace(/\./g, ''); // D.K. -> dk  (must come after the [^...] pass)
  s = s.replace(/-/g, ' ');
  const tokens = s.split(/\s+/).filter(Boolean).filter((t) => !SUFFIXES.has(t));
  return tokens.join('');
}

/** Normalized form retaining token boundaries, for token-level comparisons. */
export function nameTokens(raw: string): string[] {
  if (!raw) return [];
  let s = raw.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  s = s.replace(/[\u2018\u2019\u0027`\u00b4]/g, '').replace(/[^a-z0-9\s.-]/g, ' ').replace(/\./g, '').replace(/-/g, ' ');
  return s.split(/\s+/).filter(Boolean).filter((t) => !SUFFIXES.has(t));
}

/**
 * Recognise a team-defense row and return the team abbreviation, or null.
 * Sources spell these a dozen ways: "Texans DST", "Houston D/ST",
 * "HOU Defense", "Houston Texans".
 */
export function matchTeamDefense(raw: string): string | null {
  const tokens = nameTokens(raw);
  if (tokens.length === 0) return null;

  const meaningful = tokens.filter((t) => !DST_TOKENS.has(t));
  const hadDstMarker = meaningful.length !== tokens.length;

  for (const token of meaningful) {
    const byName = TEAM_NAMES[token];
    if (byName) return byName;
  }
  // Only trust a bare abbreviation when an explicit DST marker was present,
  // otherwise "Kansas City" style rows and player surnames get confused.
  if (hadDstMarker) {
    for (const token of meaningful) {
      const abbr = normalizeTeam(token);
      if (abbr && abbr !== 'FA') return abbr;
    }
  }
  return null;
}

/** Jaro-Winkler similarity in [0,1]. Chosen over Levenshtein because it is
 *  markedly better on human names: it rewards a shared prefix and tolerates
 *  transpositions, which is exactly how misspelled player names fail. */
export function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;

  const matchWindow = Math.max(0, Math.floor(Math.max(a.length, b.length) / 2) - 1);
  const aMatched = new Array<boolean>(a.length).fill(false);
  const bMatched = new Array<boolean>(b.length).fill(false);

  let matches = 0;
  for (let i = 0; i < a.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, b.length);
    for (let j = start; j < end; j++) {
      if (bMatched[j] || a[i] !== b[j]) continue;
      aMatched[i] = true;
      bMatched[j] = true;
      matches++;
      break;
    }
  }
  if (matches === 0) return 0;

  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < a.length; i++) {
    if (!aMatched[i]) continue;
    while (!bMatched[k]) k++;
    if (a[i] !== b[k]) transpositions++;
    k++;
  }
  transpositions /= 2;

  const jaro =
    (matches / a.length + matches / b.length + (matches - transpositions) / matches) / 3;

  let prefix = 0;
  for (let i = 0; i < Math.min(4, a.length, b.length); i++) {
    if (a[i] === b[i]) prefix++;
    else break;
  }
  return jaro + prefix * 0.1 * (1 - jaro);
}

export type MatchMethod = 'alias' | 'exact' | 'exact+context' | 'defense' | 'fuzzy' | 'none';

export interface MatchResult {
  player_key: PlayerId | null;
  method: MatchMethod;
  score: number;
  /** Populated whenever the match failed or was low-confidence. */
  suggestions: { player_key: PlayerId; full_name: string; score: number }[];
}

/**
 * A prepared index over the player universe. Building it is O(n); matching a
 * row against it is close to O(1) for the exact paths and O(n) only for the
 * fuzzy fallback, which most rows never reach.
 */
export class PlayerIndex {
  private readonly byNormalized = new Map<string, Player[]>();
  private readonly byDefenseTeam = new Map<string, Player>();
  private readonly candidates: { player: Player; normalized: string }[] = [];

  readonly universe: PlayerUniverse;
  /** FR-49: `config/aliases.yml` contents — normalized name → Sleeper id. */
  private readonly aliases: Map<string, PlayerId>;

  constructor(universe: PlayerUniverse, aliases: Map<string, PlayerId> = new Map()) {
    this.universe = universe;
    this.aliases = aliases;
    for (const player of universe.values()) {
      if (player.position === 'DEF') {
        const abbr = normalizeTeam(player.team) ?? normalizeTeam(player.player_id_sleeper);
        if (abbr) this.byDefenseTeam.set(abbr, player);
      }
      const norm = player.normalized_name;
      if (!norm) continue;
      const bucket = this.byNormalized.get(norm);
      if (bucket) bucket.push(player);
      else this.byNormalized.set(norm, [player]);
      this.candidates.push({ player, normalized: norm });
    }
  }

  /** How many players are indexed. Used by `doctor` output. */
  get size(): number {
    return this.candidates.length;
  }

  /**
   * Resolve one external row to a Sleeper id (FR-17). The ladder is ordered by
   * decreasing certainty; each rung is cheap and unambiguous before the next is
   * tried, and the fuzzy rung is gated by `threshold`.
   */
  match(
    rawName: string,
    ctx: { position?: Position | null; team?: string | null } = {},
    threshold = 0.86,
  ): MatchResult {
    const cleaned = stripAnnotations(rawName);
    const norm = normalizeName(cleaned);
    const position = ctx.position ?? null;
    const team = normalizeTeam(ctx.team ?? null);

    // 1. Manual alias overrides always win — a correction made once persists.
    const alias = this.aliases.get(norm) ?? this.aliases.get(normalizeName(rawName));
    if (alias && this.universe.has(alias)) {
      return { player_key: alias, method: 'alias', score: 1, suggestions: [] };
    }

    // 2. Team defenses are a separate namespace and never fuzzy-matched.
    //
    // The name-shaped test only applies when the source did not tell us the
    // position. Plenty of real players carry a city in their name — Justin
    // Houston, Mike Washington Jr. — and testing the name alone resolved them
    // to that city's *defense*, silently putting a DST on the board where a
    // running back should be. A row that says "RB" is never a defense no
    // matter what the surname looks like; a row with no position at all is
    // still allowed to be identified by name, which is how "Ravens D/ST" in an
    // unlabelled paste keeps working.
    if (position === 'DEF' || (position === null && matchTeamDefense(cleaned))) {
      const abbr = matchTeamDefense(cleaned) ?? team;
      const def = abbr ? this.byDefenseTeam.get(abbr) : undefined;
      if (def) {
        return { player_key: def.player_id_sleeper, method: 'defense', score: 1, suggestions: [] };
      }
    }

    // 3. Exact normalized match. Disambiguate collisions by position/team.
    const exact = this.byNormalized.get(norm);
    if (exact && exact.length === 1) {
      return { player_key: exact[0]!.player_id_sleeper, method: 'exact', score: 1, suggestions: [] };
    }
    if (exact && exact.length > 1) {
      const narrowed = this.disambiguate(exact, position, team);
      if (narrowed) {
        return {
          player_key: narrowed.player_id_sleeper,
          method: 'exact+context',
          score: 1,
          suggestions: [],
        };
      }
      // Ambiguous by name alone and context did not resolve it: report rather
      // than guess, but hand the UI every colliding candidate.
      return {
        player_key: null,
        method: 'none',
        score: 0,
        suggestions: exact.map((p) => ({
          player_key: p.player_id_sleeper,
          full_name: p.full_name,
          score: 1,
        })),
      };
    }

    // 4. Fuzzy. Context acts as a tiebreaker and a small bonus, never as a
    //    filter — sources disagree about teams constantly in the preseason.
    const scored = this.topFuzzy(norm, position, team, 5);
    const best = scored[0];
    if (best && best.score >= threshold) {
      const runnerUp = scored[1];
      // Refuse a fuzzy match that is essentially a coin flip between two people.
      if (!runnerUp || best.score - runnerUp.score >= 0.02) {
        return {
          player_key: best.player_key,
          method: 'fuzzy',
          score: best.score,
          suggestions: scored.slice(1),
        };
      }
    }

    return { player_key: null, method: 'none', score: best?.score ?? 0, suggestions: scored };
  }

  private disambiguate(players: Player[], position: Position | null, team: string | null): Player | null {
    let pool = players;
    if (position) {
      const byPos = pool.filter(
        (p) => p.position === position || p.fantasy_positions.includes(position),
      );
      if (byPos.length === 1) return byPos[0]!;
      if (byPos.length > 0) pool = byPos;
    }
    if (team) {
      const byTeam = pool.filter((p) => normalizeTeam(p.team) === team);
      if (byTeam.length === 1) return byTeam[0]!;
      if (byTeam.length > 0) pool = byTeam;
    }
    // Last resort: prefer the player Sleeper considers more relevant. This is
    // how "the active starter" beats "the retired namesake" (see Ray Rice vs.
    // Rashee Rice in the real payload).
    const active = pool.filter((p) => p.status === 'Active');
    const ranked = (active.length ? active : pool)
      .slice()
      .sort((a, b) => (a.search_rank ?? Infinity) - (b.search_rank ?? Infinity));
    const top = ranked[0];
    const second = ranked[1];
    if (top && (!second || (top.search_rank ?? Infinity) < (second.search_rank ?? Infinity))) {
      return top;
    }
    return null;
  }

  private topFuzzy(
    norm: string,
    position: Position | null,
    team: string | null,
    limit: number,
  ): { player_key: PlayerId; full_name: string; score: number }[] {
    const out: { player_key: PlayerId; full_name: string; score: number }[] = [];
    for (const { player, normalized } of this.candidates) {
      // Cheap length gate before the O(n*m) comparison.
      if (Math.abs(normalized.length - norm.length) > 5) continue;
      let score = jaroWinkler(norm, normalized);
      if (score < 0.75) continue;
      if (position && (player.position === position || player.fantasy_positions.includes(position))) {
        score += 0.03;
      } else if (position && player.position) {
        score -= 0.06;
      }
      if (team && normalizeTeam(player.team) === team) score += 0.03;
      if (player.status !== 'Active') score -= 0.04;
      out.push({ player_key: player.player_id_sleeper, full_name: player.full_name, score: Math.min(score, 1) });
    }
    out.sort((a, b) => b.score - a.score);
    return out.slice(0, limit);
  }
}

export interface MatchReport {
  source_id: string;
  rows_read: number;
  rows_matched: number;
  rows_unmatched: number;
  match_rate: number;
  by_method: Record<MatchMethod, number>;
  unmatched: UnmatchedRow[];
}

/** Aggregate match statistics for `doctor` (FR-20) and the import summary (§5.3). */
export function summarizeMatches(
  source_id: string,
  results: { raw_name: string; overall_rank: number; team: string | null; position: Position | null; result: MatchResult }[],
): MatchReport {
  const by_method: Record<MatchMethod, number> = {
    alias: 0, exact: 0, 'exact+context': 0, defense: 0, fuzzy: 0, none: 0,
  };
  const unmatched: UnmatchedRow[] = [];
  let matched = 0;

  for (const r of results) {
    by_method[r.result.method]++;
    if (r.result.player_key) {
      matched++;
    } else {
      unmatched.push({
        raw_name: r.raw_name,
        overall_rank: r.overall_rank,
        team: r.team,
        position: r.position,
        suggestions: r.result.suggestions,
      });
    }
  }

  return {
    source_id,
    rows_read: results.length,
    rows_matched: matched,
    rows_unmatched: results.length - matched,
    match_rate: results.length === 0 ? 0 : matched / results.length,
    by_method,
    unmatched,
  };
}
